// AI Product Recommender engine — DETERMINISTIC, no LLM.
// run(answers, { recipe, catalog }) -> { lane, primary, showAlternative, broad, focusedIds, prices, dropped, upsell, trace }
//
// Ported from the PoC spike engine, redesigned 2026-07-21 on product feedback, then the
// ENGLISH lane was RE-BASELINED 2026-07-24 on the marketing team's confirmed business
// rules. The 2026-07-23 soft-score model (fixed-weight lead, Ultimate bump, bundle-guard,
// option D, premium-movie floor) is RETIRED for English — marketing rejected its premises
// (notably "Ultimate is the movies/entertainment tier", which the sales data disproved).
// The SPANISH lane is deliberately UNCHANGED (BK: "spanish 레인 로직 무변경"): it keeps the
// 2026-07-23 soft-score lead verbatim, so this file carries both models — new for English,
// frozen-legacy for Spanish. The lane decision below mirrors the old prune-before-flip
// ordering so Spanish routing is preserved exactly; a parity harness (classifying combos by
// the OLD engine's actual lane, not a heuristic) proves the Spanish outputs are byte-identical
// to the pre-2026-07-24 engine GIVEN THE SAME priceGapThreshold. That qualifier is load-bearing:
// `threshold` is shared by both lanes and DEFAULT_PRICE_GAP_THRESHOLD moved 5 -> 15 on
// 2026-07-27, so a recipe with the field blank does change Spanish. Measured effect: a pack
// $5.01–$15.00 under Óptimo Más loses the price-advantage point (focused 3 -> 2) and now shows
// TWO cards where it showed one. The lead itself does not move — the Spanish theme-fit edge
// still carries focused 2 vs 0 — so this is a card-count change, not a lead change. The live
// CMS story authors 15, so the production path never takes the default; the harness compared
// authored runs. See the Spanish close-call tests in engine.spec.ts for the pinned behavior.
// Prices / SVOD bundles / the sports-tier anchor always come ONLY from the injected `catalog`,
// never from the recipe.
//
// ---------------- Marketing-confirmed English rules (2026-07-24) ----------------
//   1. Q3 (live-sports importance) is CONDITIONAL — authored to show only when Sports is a
//      selected interest (CMS show_if on Q2's Sports option; soft-hide pruning drops answers
//      to hidden questions). The engine also only ACTS on live-sports importance when a
//      live-sports theme is selected, so a stray answer on a mis-authored recipe can never
//      distort a non-sports result. There is no "not important -> drop the pick" rule for
//      English anymore: "somewhat" and "not" behave identically; only "very" carries a signal.
//   2. Live sports "very" -> the broad card floors at the sports tier (Choice, the structural
//      RSN minimum from catalog.liveSports.minSignatureId) AND the genre packs that cover
//      sports are disqualified as the focused card: MySports has no RSNs, so a serious sports
//      fan is never led to it (the RSN gap). "somewhat"/"not" leave the sports packs eligible
//      and add no floor.
//   3. Ultimate is RETIRED from the recommendation ladder (RECOMMENDATION_EXCLUDED_SIGNATURE_
//      IDS). The broad ladder is Entertainment < Choice < Premier. Ultimate is still a real
//      catalog product; it is simply never recommended here.
//   4. LEAD = coverage filter, then lowest price. The focused (à-la-carte) candidate is the
//      CHEAPEST single product — one base genre pack or one CMS combo — that covers EVERY
//      selected English interest; if none covers them all, there is no focused candidate and
//      the broad signature leads. Between a covering pack and the broad signature, the CHEAPER
//      one leads. The Q1 preference (prefer:broad / prefer:focused) only breaks a CLOSE call —
//      a price gap within recipe.priceGapThreshold ($15 default); beyond that, price decides.
//      (The old bundle-guard is absorbed: pack >= signature means the signature is the cheaper
//      card and leads on its own.)
//   5. Price level floors the broad ladder by LIVE ladder position (never dollars):
//      essentials/budget -> cheapest tier, balanced/midrange -> middle rung, most-complete/
//      flagship -> top rung. On the Ultimate-excluded 3-rung ladder this is Entertainment /
//      Choice / Premier. Floors combine by MAX with the sports-"very" floor. No-signal corners
//      still resolve to this default ladder. A fresh run can no longer produce one — every
//      step, multi-select included, requires a pick before Next enables — but a results row
//      stored by an older build can still restore an answer set with no interest picks, and
//      engine.spec exercises the corner directly.
//
// Phase 1 refactor (kept): effect scan over VISIBLE questions only; show_if conditions
// (AND, ops is/includes/is-not); soft-hide pruning; themePolicy from the CMS themes[] table.

import {
  Answers,
  Catalog,
  CatalogPackage,
  Lane,
  Recipe,
  RecipeOption,
  RecipeQuestion,
  RecipeTheme,
  RunResult,
  ShowIfCondition
} from './engine.types';

function selectedOptions(q: RecipeQuestion, answers: Answers): RecipeOption[] {
  return (answers[q.id] || [])
    .map(oid => q.options.find(o => o.id === oid))
    .filter(Boolean) as RecipeOption[];
}

function evalShowIf(cond: ShowIfCondition, kept: Answers): boolean {
  const sel = kept[cond.qId] || [];
  switch (cond.op) {
    case 'is':
      return sel.length === 1 && sel[0] === cond.optId;
    case 'includes':
      return sel.includes(cond.optId);
    case 'is-not':
      return !sel.includes(cond.optId);
    default:
      return false;
  }
}

// Which questions are visible given the answers so far. Single top-down pass:
// conditions may only reference EARLIER questions (forward/self refs never match),
// and they only see answers of questions that are themselves visible — so hiding
// a branch collapses everything that depended on it (soft-hide chains).
export function visibleQuestions(recipe: Recipe, answers: Answers): RecipeQuestion[] {
  const vis: RecipeQuestion[] = [];
  const kept: Answers = {};
  for (const q of recipe.questions) {
    const show = (q.show_if || []).every(c => evalShowIf(c, kept));
    if (show) {
      vis.push(q);
      if ((answers[q.id] || []).length) {
        kept[q.id] = answers[q.id];
      }
    }
  }
  return vis;
}

interface ThemePolicyEntry {
  focused: string;
  premiumMovie: boolean;
  spanishFlip: boolean;
  streamingBundle: boolean;
  liveSports: boolean;
}

// themes[] table -> lookup map. Empty names are skipped; on duplicate names the
// later row wins (validate-recipe warns on both).
function buildThemePolicy(themes: RecipeTheme[]): Record<string, ThemePolicyEntry> {
  const tp: Record<string, ThemePolicyEntry> = {};
  for (const t of themes || []) {
    if (!t.name || !t.name.trim()) {
      continue;
    }
    tp[t.name] = {
      focused: t.packId,
      premiumMovie: !!t.premiumMovie,
      spanishFlip: !!t.spanishFlip,
      streamingBundle: !!t.streamingBundle,
      liveSports: !!t.liveSports
    };
  }
  return tp;
}

export type PriceLevel = 'budget' | 'midrange' | 'flagship';

/**
 * Default $ gap under which the runner-up card is shown next to the lead.
 * 15, matching the value marketing signed off on (D6, 2026-07-27) and the live CMS field.
 * The fallback deliberately equals the authored value so an empty CMS field behaves like the
 * agreed rule instead of silently narrowing the compare window.
 */
export const DEFAULT_PRICE_GAP_THRESHOLD = 15;

// Signatures excluded from the recommendation ladder (marketing rule 3, 2026-07-24):
// Ultimate is retired as a recommendable tier — the sales data showed its incremental
// value is sports depth (NHL Network / CBS Sports / FS2), which serious sports fans reach
// through the Choice-floor + sports upsell, and its conversion was the lowest of the ladder.
// It stays a real catalog product; it is only removed from what we RECOMMEND. Keyed by
// canonical id (a known v1 hardcode, like the upsell copy).
// Typed `readonly` to state the intent and make `.push()` a compile error for importers. It is
// NOT a runtime guarantee — the value is a plain mutable array and `as any` or plain JS gets
// straight past it. If this ever needs enforcing at runtime, Object.freeze it.
//
// Note it is a no-op against today's live prices: on the 4-tier ladder
// ($59.99/$64.99/$89.99/$129.99) Ultimate is never the cheapest tier at or above any floor
// (midrange's middle rung is Choice at 3 or 4 tiers, flagship's top is Premier). Verified by
// removing the exclusion and re-running all 2268 answer combinations: 0 changed. It is a
// defensive rule, not dead code — a 5th tier shifts the middle index onto it.
export const RECOMMENDATION_EXCLUDED_SIGNATURE_IDS: readonly string[] = ['ATT_TV_ULTIMATE'];

// Lead-score weights — SPANISH LANE ONLY since 2026-07-24 (the English lane no longer
// scores; it filters on coverage then picks the lowest price). Kept verbatim from the
// 2026-07-23 model so the Spanish lane (Óptimo Más vs the Spanish genre pack) is unchanged:
// prefer 3 / theme fit 2 to the better-covering card / price level 2 / price advantage 1;
// ties resolve toward an explicit Q1 preference, else to broad.
const W_PREFER = 3;
const W_THEME_FULL = 2;
const W_PRICE_LEVEL = 2;
const W_PRICE_ADVANTAGE = 1;

export type LiveSportsLevel = 'very' | 'somewhat' | 'not';

interface ExtractedEffects {
  prefer: 'broad' | 'focused' | null;
  preferFrom: string | null;
  spanishLane: boolean;
  priceLevel: PriceLevel | null;
  liveSports: LiveSportsLevel | null;
  themes: string[];
}

// Effect scan over visible questions. 'prefer:*'/'price:*'/'live_sports:*' assign in
// scan order (last wins — validate-recipe warns on layouts where that is ambiguous),
// 'lane:spanish' latches, 'theme:*' accumulates unique names. Unknown kinds / unknown
// values / 'none' are ignored (validate-recipe warns at author time — severity realigned
// 2026-08-03: both typo shapes are inert HERE; see validate-recipe's theme check for the
// one place an orphan theme NAME is not — the Spanish-alone lane denominator).
function extractEffects(visible: RecipeQuestion[], answers: Answers): ExtractedEffects {
  let prefer: 'broad' | 'focused' | null = null;
  let preferFrom: string | null = null;
  let spanishLane = false;
  let priceLevel: PriceLevel | null = null;
  let liveSports: LiveSportsLevel | null = null;
  const themes: string[] = [];
  for (const q of visible) {
    for (const o of selectedOptions(q, answers)) {
      if (!o.effect || o.effect === 'none') {
        continue;
      }
      const [kind, val] = o.effect.split(':');
      if (kind === 'prefer' && (val === 'broad' || val === 'focused')) {
        prefer = val;
        preferFrom = o.label;
      } else if (kind === 'lane' && val === 'spanish') {
        spanishLane = true;
      } else if (kind === 'price' && (val === 'budget' || val === 'midrange' || val === 'flagship')) {
        priceLevel = val;
      } else if (kind === 'live_sports' && (val === 'very' || val === 'somewhat' || val === 'not')) {
        liveSports = val;
      } else if (kind === 'theme') {
        if (!themes.includes(val)) {
          themes.push(val);
        }
      }
    }
  }
  return {
    prefer,
    preferFrom,
    spanishLane,
    priceLevel,
    liveSports,
    themes
  };
}

const allPkgs = (catalog: Catalog): CatalogPackage[] => [
  catalog.spanishSignature,
  ...catalog.signatures,
  ...catalog.genrePacks
];
const pkg = (catalog: Catalog, id: string) => allPkgs(catalog).find(p => p.id === id);
const label = (catalog: Catalog, id: string) => (pkg(catalog, id) || ({} as CatalogPackage)).label || id;
const isMini = (catalog: Catalog, id: string) => !!(catalog.genrePacks.find(g => g.id === id) || ({} as { mini?: boolean })).mini;
const price = (catalog: Catalog, id: string): number | null => {
  const p = pkg(catalog, id);
  return p && p.price != null ? p.price : null;
};
// null if any id is unknown (don't silently price it as $0)
const sumP = (catalog: Catalog, ids: string[]): number | null => {
  if (!ids.length) {
    return null;
  }
  if (ids.some(id => price(catalog, id) == null)) {
    return null;
  }
  return ids.reduce((s, id) => s + (price(catalog, id) as number), 0);
};

// A candidate for the English focused (à-la-carte) card: one sellable product (a base
// genre pack, or a CMS combo product) plus the set of theme NAMES it covers.
interface FocusedProduct {
  id: string;
  price: number | null;
  covers: Set<string>;
}

// Card-visibility trace line for the Spanish lane, one line per outcome like the English
// lane's tail — the configurator reads the trace as the full explanation, and "nothing
// said" reads as "nothing decided". Wording restored from the pre-split shared tail;
// lives outside run() only to keep run under the lint complexity cap. A single object
// argument on purpose: four positional booleans transpose silently. `gap` is meaningless
// (pass 0) when gapKnown is false.
function spanishCardsTrace(outcome: {
  gapKnown: boolean;
  guardShows: boolean;
  dropForced: boolean;
  showAlternative: boolean;
  gap: number;
  threshold: number;
}): string {
  const { gapKnown, guardShows, dropForced, showAlternative, gap, threshold } = outcome;
  if (!gapKnown) {
    return 'Cards -> lead only (missing live price on one side; no comparison shown)';
  }
  if (guardShows) {
    return 'Cards -> both (bundle-guard overrode the picks; show the side-by-side)';
  }
  if (dropForced) {
    return 'Cards -> both (the lead pack covers fewer of the stated interests; the covering card shows for comparison)';
  }
  if (showAlternative) {
    return `Cards -> both (price gap $${gap.toFixed(2)} <= $${threshold} — close call, let the shopper compare)`;
  }
  return `Cards -> lead only (price gap $${gap.toFixed(2)} > $${threshold})`;
}

export function run(
  answers: Answers,
  deps: { recipe: Recipe; catalog: Catalog }
): RunResult {
  const { recipe, catalog } = deps;
  const trace: string[] = [];
  const upsell: string[] = [];
  const dropped: string[] = [];
  const tp = buildThemePolicy(recipe.themes);

  // Only answers on visible questions feed the mapping (soft-hide pruning).
  const fx = extractEffects(visibleQuestions(recipe, answers), answers);
  let lane: Lane = fx.spanishLane ? 'spanish' : 'english';

  let { themes } = fx;
  // Spanish-alone flip: if every selected theme is a spanish-flip theme (>=1) -> Spanish
  // universe regardless. The lane decision deliberately mirrors the PRE-2026-07-24 ordering:
  // the old engine pruned live_sports:'not' themes BEFORE this flip check, so e.g.
  // Sports+Spanish+"not important" routes to Spanish (Sports prunes away, leaving only the
  // Spanish-flip theme). Keeping that ordering makes Spanish routing byte-for-byte unchanged
  // ("무변경"). The English lane below intentionally does NOT drop those themes (marketing
  // rule 1) — this prune is scoped to lane routing, nothing else.
  const themesForLane = fx.liveSports === 'not' ?
    themes.filter(t => !(tp[t] && tp[t].liveSports)) :
    themes;
  const flips = themesForLane.filter(t => tp[t] && tp[t].spanishFlip);
  if (themesForLane.length && flips.length === themesForLane.length) {
    lane = 'spanish';
  }
  trace.push(`Lane = ${lane}`);

  let broad: CatalogPackage;
  let focusedIds: string[];
  let primary: 'broad' | 'focused' = 'broad';
  let showAlternative = false;
  let bPrice: number | null;
  let fPrice: number | null;
  const threshold =
    recipe.priceGapThreshold != null && Number.isFinite(recipe.priceGapThreshold) && recipe.priceGapThreshold >= 0 ?
      recipe.priceGapThreshold :
      DEFAULT_PRICE_GAP_THRESHOLD;

  if (lane === 'spanish') {
    // ================= SPANISH LANE — FROZEN (pre-2026-07-24 soft-score, unchanged) =================
    // live_sports:not drop survives HERE only (English dropped it, marketing rule 1): the
    // Spanish lane's recommendation OUTPUTS are byte-for-byte the old engine's (the diagnostic
    // `trace` differs; the spanish-parity harness excludes it and proves the rest identical).
    if (fx.liveSports === 'not') {
      themes = themes.filter(t => !(tp[t] && tp[t].liveSports));
    }
    broad = catalog.spanishSignature;
    themes.filter(t => !(tp[t] && tp[t].spanishFlip)).forEach(t => dropped.push(`${t} (English)`));
    trace.push(`Signature -> ${broad.label} (Spanish lane; no English escalation)`);
    if (fx.priceLevel) {
      trace.push(`Price level "${fx.priceLevel}" -> no ladder to floor in the Spanish lane (single Spanish signature)`);
    }
    const coveredThemes = new Set<string>();
    const sp = catalog.genrePacks.find(g => g.group === 'spanish');
    focusedIds = sp ? [sp.id] : [];
    if (sp) {
      themes.forEach(t => {
        if (tp[t] && tp[t].focused === sp.id) {
          coveredThemes.add(t);
        }
      });
    }
    trace.push(`Genre Pack -> ${focusedIds.map(id => label(catalog, id)).join(' + ') || '—'}`);

    bPrice = broad ? broad.price : null;
    fPrice = sumP(catalog, focusedIds);
    let broadCoversMore = false;
    let focusedCoversAllPicks = true;
    let sb = 0;
    let sf = 0;
    if (fx.prefer === 'broad') {
      sb += W_PREFER;
    } else if (fx.prefer === 'focused') {
      sf += W_PREFER;
    }
    const themesWithPacks = themes.filter(t => tp[t] && tp[t].focused && pkg(catalog, tp[t].focused));
    if (themesWithPacks.length) {
      const focusedCovers = themesWithPacks.filter(t => coveredThemes.has(t)).length;
      const broadCovers = themesWithPacks.filter(t => !!(tp[t] && tp[t].spanishFlip)).length;
      broadCoversMore = broadCovers > focusedCovers;
      focusedCoversAllPicks = focusedCovers === themesWithPacks.length;
      if (focusedCovers === themesWithPacks.length) {
        sf += W_THEME_FULL;
      } else if (broadCovers > focusedCovers) {
        sb += W_THEME_FULL;
      }
    }
    if (fx.priceLevel === 'flagship') {
      sb += W_PRICE_LEVEL;
    } else if (fx.priceLevel === 'budget' && bPrice != null && fPrice != null && bPrice !== fPrice) {
      if (fPrice < bPrice) {
        sf += W_PRICE_LEVEL;
      } else {
        sb += W_PRICE_LEVEL;
      }
    }
    if (bPrice != null && fPrice != null && Math.abs(bPrice - fPrice) > threshold) {
      if (fPrice < bPrice) {
        sf += W_PRICE_ADVANTAGE;
      } else {
        sb += W_PRICE_ADVANTAGE;
      }
    }
    primary = sf > sb || (sf === sb && fx.prefer === 'focused') ? 'focused' : 'broad';
    trace.push(`Lead -> ${primary} · broad ${sb} vs focused ${sf}${sb === sf ? (fx.prefer === 'focused' ? ' · tie goes to the stated focused preference' : ' · tie goes to broad') : ''}`);
    if (primary === 'focused' && fPrice == null) {
      primary = 'broad';
      trace.push('Lead override -> broad · the focused card has no live price and unpriced cards never lead');
    }
    let bundleWins = false;
    if (bPrice != null && fPrice != null && fPrice >= bPrice) {
      bundleWins = true;
      primary = 'broad';
      trace.push(`Bundle-guard: Genre Pack $${fPrice.toFixed(2)} >= Signature $${bPrice.toFixed(2)} -> recommend the Signature bundle; Genre Pack kept intact as the alternative`);
    }
    const gapKnown = bPrice != null && fPrice != null;
    const gap = gapKnown ? Math.abs((bPrice as number) - (fPrice as number)) : 0;
    const dropForced = primary === 'focused' && broadCoversMore;
    const guardShows = bundleWins && focusedCoversAllPicks;
    showAlternative = gapKnown && (guardShows || dropForced || gap <= threshold);
    trace.push(spanishCardsTrace({
      gapKnown,
      guardShows,
      dropForced,
      showAlternative,
      gap,
      threshold
    }));
  } else {
    // ================= ENGLISH LANE — marketing rules (2026-07-24) =================
    // `tp[t]` must be required, not just checked for spanishFlip. A theme name with no
    // themes-table row can reach here two ways — a renamed/deleted CMS row, or (routinely)
    // crossCheckRecipeCatalog dropping a row whose pack left packageConfig, which it does at
    // WARN level while leaving the option's `theme:<name>` effect in place. Keeping the orphan name
    // in coverageThemes made it UNCOVERABLE rather than inert: no product can cover it, so
    // `qualifying` empties and the focused card vanishes for every shopper who picks that
    // option. Measured on the live story, removing one genre pack from packageConfig cost the
    // focused card in 54 of 1665 answer combinations, with no error to surface it. Dropping
    // the orphan restores the documented contract — a dead theme's effect is a no-op, and the
    // shopper still gets a card covering their remaining live interests.
    const coverageThemes = themes.filter(t => tp[t] && !tp[t].spanishFlip);
    const asideThemes = themes.filter(t => tp[t] && tp[t].spanishFlip);

    // Live sports matter to the pick ONLY when a live-sports theme is selected (Q3 shows
    // only then; this also fails safe on a mis-authored recipe).
    const sportsThemeSelected = coverageThemes.some(t => tp[t] && tp[t].liveSports);
    const sportsVery = sportsThemeSelected && fx.liveSports === 'very';
    if (sportsThemeSelected) {
      trace.push(
        sportsVery ?
          'Live sports "very" -> Choice floor + NO à-la-carte card (every product covering a sports interest is disqualified: no RSNs on MySports)' :
          `Live sports "${fx.liveSports || 'unspecified'}" -> no floor, sports packs stay eligible`
      );
    } else if (fx.liveSports) {
      // The answer exists but no live-sports theme is selected, so it is ignored. Without this
      // line the trace is silent about an answer the shopper gave — the configurator reads the
      // trace as the full explanation, and "nothing said" reads as "nothing answered".
      trace.push(`Live sports "${fx.liveSports}" -> ignored (no live-sports interest selected)`);
    }

    // ---------------- BROAD (signature) card ----------------
    // Cheapest recommendable signature at or above the combined rank floor. Floors: price
    // level by live ladder position, and the sports-"very" RSN minimum. Combined by MAX.
    // Ultimate is excluded from the ladder (marketing rule 3).
    const sigs = catalog.signatures
      .filter(s => !RECOMMENDATION_EXCLUDED_SIGNATURE_IDS.includes(s.id))
      // .slice() is redundant behind .filter() today, but catalog.signatures is a prop that
      // also ships in __NEXT_DATA__ — keep the copy explicit so dropping the filter can never
      // turn this into an in-place sort of shared state.
      .slice()
      .sort((a, b) => (a.rank || 0) - (b.rank || 0));
    // Every broad path below dereferences `broad`, so an empty ladder is a contract breach,
    // not a shape to render around. adaptCatalog cannot produce one (it errors when Choice is
    // missing or unpriced, and the component bails on any error), but a hand-built catalog can
    // — e.g. one whose only priced signature is the excluded Ultimate. Fail loudly here rather
    // than as "Cannot read properties of undefined (reading 'label')" three lines down.
    if (!sigs.length) {
      // "empty or every entry excluded" — precisely what the filter above can produce.
      // Do not say "none priced": `sigs` is filtered by the exclusion list only, so an
      // unpriced signature is still in this list and this branch never fires for it.
      throw new Error('no recommendable signature in catalog (signatures empty, or every entry is in RECOMMENDATION_EXCLUDED_SIGNATURE_IDS)');
    }
    let floorRank = 0;
    if (sportsVery) {
      const sportsSig = sigs.find(s => s.id === catalog.liveSports.minSignatureId);
      if (!sportsSig) {
        // Fires when catalog.liveSports.minSignatureId names an EXCLUDED signature too — the
        // anchor (LIVE_SPORTS_MIN_SIGNATURE_ID in catalog-adapter) and this exclusion list are
        // hardcoded in two files with no build-time cross-check, so say so in the message.
        throw new Error(`catalog.liveSports.minSignatureId not found in recommendable signatures (excluded, or absent from the catalog): ${catalog.liveSports.minSignatureId}`);
      }
      floorRank = Math.max(floorRank, sportsSig.rank || 0);
      trace.push(`Live sports "very" -> floor ${sportsSig.label} (sports depth starts there)`);
    }
    if (fx.priceLevel === 'flagship' && sigs.length) {
      const top = sigs[sigs.length - 1];
      floorRank = Math.max(floorRank, top.rank || 0);
      trace.push(`Price level "flagship" -> floor ${top.label} (top of the live ladder)`);
    } else if (fx.priceLevel === 'midrange' && sigs.length) {
      const mid = sigs[Math.floor((sigs.length - 1) / 2)];
      floorRank = Math.max(floorRank, mid.rank || 0);
      trace.push(`Price level "midrange" -> floor ${mid.label} (middle of the live ladder)`);
    } else if (fx.priceLevel === 'budget') {
      trace.push('Price level "budget" -> cheapest recommendable tier (no floor)');
    }
    broad = sigs.find(s => (s.rank || 0) >= floorRank) || sigs[sigs.length - 1];
    trace.push(
      floorRank > 0 ?
        `Signature -> ${broad.label} (cheapest recommendable tier at or above the floor)` :
        `Signature -> ${broad.label} (national content is ~the same across Signatures -> cheapest)`
    );

    // ---------------- FOCUSED (à-la-carte) card ----------------
    // Candidate products = real base genre packs (non-mini) + CMS combo products, each
    // carrying the theme names it covers. The focused candidate is the CHEAPEST single
    // product that covers EVERY coverage theme (marketing rule 4); sports packs are
    // disqualified when live sports are "very". No qualifier -> the signature leads.
    const baseCovers: Record<string, Set<string>> = {};
    for (const t of recipe.themes || []) {
      const pid = tp[t.name] && tp[t.name].focused;
      if (pid && !tp[t.name].spanishFlip && pkg(catalog, pid) && !isMini(catalog, pid)) {
        (baseCovers[pid] = baseCovers[pid] || new Set()).add(t.name);
      }
    }
    const products: FocusedProduct[] = Object.keys(baseCovers).map(id => ({
      id,
      price: price(catalog, id),
      covers: baseCovers[id]
    }));
    // The spanishFlip check has to be here too, not just on the base packs above. A combo
    // qualifies by covering every COVERAGE theme, and coverage themes exclude the spanish-flip
    // ones — so a row listing a Spanish theme is judged only on its English themes and its
    // extra Spanish cover is free. Measured (2026-07-30): adding one row
    // `['Sports','Spanish'] -> MiEspanol` made an English shopper who tapped Sports
    // ALONE lead with MiEspanol instead of MySports. It also survives tapping
    // Sports+Spanish, because the lane only flips to Spanish when EVERY selected theme is a
    // flip theme. Skip the whole row rather than dropping the Spanish name from `covers`:
    // measured, that variant does NOT fix a 3-theme row — `['Sports','News','Spanish']` then
    // competes as a Sports+News product and still beats the real Duo on price.
    // No-op against today's recipe (its two rows list no Spanish theme): 0 drift over 2268
    // answer combinations. Approved as a forward-looking rule change 2026-07-30.
    // Unknown names get the same orphan tolerance as coverageThemes above: a theme row
    // removed by crossCheckRecipeCatalog drops out of the combo row instead of vetoing it
    // (approved 2026-07-31); fewer than two surviving names cannot outcover a single pack.
    for (const c of recipe.combos || []) {
      const names = (c.themes || []).filter(n => n && tp[n]);
      // Count what the row actually COVERS, not how many entries it lists: a row that repeats
      // a theme covers one theme, and gating on names.length would let ['Sports','Sports']
      // compete as a two-theme product. Derive both from the same set so they cannot drift.
      const covers = new Set(names);
      const englishOnly = names.every(n => !tp[n].spanishFlip);
      if (covers.size >= 2 && englishOnly && c.packId && pkg(catalog, c.packId) && !isMini(catalog, c.packId)) {
        products.push({
          id: c.packId,
          price: price(catalog, c.packId),
          covers
        });
      }
    }
    // sportsVery alone is the disqualifier, and it is TOTAL — there is no per-product test to
    // make here. sportsVery only turns on when a live-sports theme is among the coverage
    // themes, and qualifying requires covering EVERY coverage theme, so every candidate that
    // could still qualify necessarily covers a live-sports theme. Filtering "sports packs"
    // therefore empties the list outright: live sports "very" means no à-la-carte card at all,
    // confirmed intended 2026-07-30. Do not reintroduce a `p.sportsPack` term — it read as a
    // narrow exclusion while behaving as an absolute one, and a future RSN-carrying sports
    // pack would be dropped by it just the same.
    const qualifying = coverageThemes.length && !sportsVery ?
      products
        .filter(p => p.price != null && coverageThemes.every(t => p.covers.has(t)))
        .sort((a, b) => (a.price as number) - (b.price as number)) :
      [];
    const focusedProduct: FocusedProduct | null = qualifying[0] || null;
    if (focusedProduct) {
      trace.push(`Genre Pack -> ${label(catalog, focusedProduct.id)} (cheapest product covering ${coverageThemes.join(' + ')})`);
    } else {
      // Say WHICH of the three reasons applied. "no single product covers X" was previously
      // emitted for the sports-"very" case too, where it is simply false — MySports does cover
      // Sports, it was disqualified. The configurator reads this trace as the explanation.
      trace.push(
        !coverageThemes.length ?
          'Genre Pack -> none (no themed picks) -> signature leads' :
          sportsVery ?
            `Genre Pack -> none (live sports "very" disqualifies every product covering ${coverageThemes.join(' + ')}) -> signature leads` :
            `Genre Pack -> none (no single product covers ${coverageThemes.join(' + ')}) -> signature leads`
      );
    }
    // No covering product -> no focused candidate (marketing rule 4): emit an empty id list
    // rather than the default pack, so the result never advertises a pack at a null price
    // (buildFocusedCtaUrl returns null on []; the broad signature is the guaranteed result).
    focusedIds = focusedProduct ? [focusedProduct.id] : [];

    // Interests the lead card does not bundle as one SKU are recorded as `dropped`: coverage
    // themes not covered by the focused product, plus Spanish-flip themes picked in the
    // English lane (outside every English product's universe). No longer shopper-facing (the
    // under-card list was removed 2026-07-30) — this is engine diagnostics now.
    // Ordered by the CMS themes table, not by `themes`: the latter comes from extractEffects
    // walking `answers[q.id]`, which the UI appends in TAP order, so identical selections in a
    // different tap order would otherwise emit a differently ordered list. Row order is CI's
    // stated priority, and a deterministic output is what the configurator diffs against.
    // Measured: reversing the tap order changed this list in 1758 combos before, 0 after.
    // The SPANISH branch above still follows answer order (444 of its 522 multi-interest combos
    // are tap-order dependent) — that is the "무변경" freeze, not an oversight. Do not "fix" it
    // here; it would break the parity harness.
    const leadPackId = focusedProduct ? focusedProduct.id : null;
    const tableOrder = (recipe.themes || []).map(t => t.name);
    const orderedForDrop = [
      ...tableOrder.filter(n => coverageThemes.indexOf(n) !== -1),
      ...tableOrder.filter(n => asideThemes.indexOf(n) !== -1)
    ];
    orderedForDrop.forEach(t => {
      const pid = tp[t] && tp[t].focused;
      if (pid && pkg(catalog, pid) && pid !== leadPackId && !(focusedProduct && focusedProduct.covers.has(t))) {
        dropped.push(label(catalog, pid));
      }
    });

    // ---------------- Lead selection (coverage filter -> lowest price) ----------------
    bPrice = broad ? broad.price : null;
    fPrice = focusedProduct ? focusedProduct.price : null;
    const gapKnown = bPrice != null && fPrice != null;
    // Compare money in integer cents so IEEE-754 error (e.g. 64.99 - 59.99 = 4.999999999999993)
    // can't flip a shopper across the close-call threshold. English lane only — the Spanish lane
    // keeps its float compares below to stay byte-identical to the pre-2026-07-24 engine.
    const gap = gapKnown ? Math.abs((bPrice as number) - (fPrice as number)) : 0;
    const withinThreshold = gapKnown && Math.round(gap * 100) <= Math.round(threshold * 100);
    if (!focusedProduct || !gapKnown) {
      primary = 'broad';
      trace.push(
        focusedProduct ?
          'Lead -> broad · the focused card has no live price and unpriced cards never lead' :
          'Lead -> broad · no covering à-la-carte product'
      );
    } else {
      const cheaper: 'broad' | 'focused' = (fPrice as number) < (bPrice as number) ? 'focused' : 'broad';
      if (withinThreshold && fx.prefer) {
        primary = fx.prefer === 'focused' ? 'focused' : 'broad';
        trace.push(`Lead -> ${primary} · close call ($${gap.toFixed(2)} <= $${threshold}) broken by the stated "${fx.preferFrom}" preference`);
      } else {
        primary = cheaper;
        trace.push(`Lead -> ${primary} · ${primary === 'focused' ? label(catalog, (focusedProduct as FocusedProduct).id) : broad.label} is the cheaper card ($${gap.toFixed(2)} gap)`);
      }
    }

    // ---------------- Card visibility ----------------
    // One card by default; the runner-up earns its slot only on a genuine near-tie: both
    // priced and within the threshold (bundle-guard/option-D side-by-sides retired with the
    // soft-score model).
    showAlternative = gapKnown && !!focusedProduct && withinThreshold;
    if (!gapKnown || !focusedProduct) {
      trace.push('Cards -> lead only (no priced runner-up to compare)');
    } else if (showAlternative) {
      trace.push(`Cards -> both (price gap $${gap.toFixed(2)} <= $${threshold} — close call, let the shopper compare)`);
    } else {
      trace.push(`Cards -> lead only (price gap $${gap.toFixed(2)} > $${threshold})`);
    }
  }

  // ---------------- Streaming-bundle annotation (v2, both lanes) ----------------
  // NOT RENDERED on the results UI as of 2026-07-30: the list this and the upsell layer below
  // write into (`upsell`) was removed from under the cards, so `streaming_bundle` and
  // `premium_movie` are authorable flags with no shopper-visible effect today. They still
  // drive `trace`, which the local configurator shows, and both stay in RunResult so a future
  // surface can render them. Restoring the inclusion line is a marketing decision, not a bug
  // fix — do not "reconnect" it here.
  // Opt-in per theme (CMS streaming_bundle flag). Surfaces the streaming subscriptions
  // already EMBEDDED in the primary card — never a lane or an escalation. Names arrive
  // verbatim from the epoch embedded-SVOD caches via catalog svods. No svod data -> no claim.
  if (themes.some(t => tp[t] && tp[t].streamingBundle)) {
    const primaryPkgs = primary === 'broad' ?
      (broad ? [broad] : []) :
      focusedIds.map(id => pkg(catalog, id)).filter(Boolean);
    const svodNames = [...new Set(primaryPkgs.reduce<string[]>((s, p) => s.concat(p.svods || []), []))];
    if (svodNames.length) {
      upsell.push(`Includes ${svodNames.join(', ')} — with a DIRECTV device`);
      trace.push(
        `Streaming theme -> ${primary === 'broad' ? (broad ? broad.label : '—') : focusedIds.map(id => label(catalog, id)).join(' + ')} embeds ${svodNames.join(', ')} (from live embedded-SVOD data; DIRECTV-device condition)`
      );
    } else {
      trace.push('Streaming theme -> no embedded streaming data for the primary card (no inclusion claim shown)');
    }
  }

  // ---------------- Upsell layer (never a 3rd card) ----------------
  // Flag-driven (no theme-name hardcodes): premiumMovie themes surface the premium note;
  // liveSports themes surface the sports-depth add-on unless the shopper said sports are
  // "not" important (that answer contradicts a sports upsell — 2026-07-24). Both English-only.
  if (lane !== 'spanish' && themes.some(t => tp[t] && tp[t].premiumMovie)) {
    upsell.push(`Premium movies: ${catalog.premiumMovieSvods.join(', ')} (Premier) or add-ons`);
  }
  if (lane !== 'spanish' && themes.some(t => tp[t] && tp[t].liveSports) && fx.liveSports !== 'not') {
    upsell.push('MySports Extra — NFL RedZone etc. (sports depth)');
  }
  [...new Set(dropped)].forEach(d => upsell.push(`Also wanted: ${d}`));

  return {
    lane,
    primary,
    showAlternative,
    broad,
    broadLabel: broad ? broad.label : '—',
    focusedIds,
    focusedLabels: focusedIds.map(id => label(catalog, id)),
    broadPrice: bPrice,
    focusedPrice: fPrice,
    dropped: [...new Set(dropped)],
    upsell,
    trace
  };
}
